import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { planningAPI, finishedProductsAPI } from '@/utils/api';
import { Plus, Trash2, ShoppingCart, AlertTriangle, FileDown, Lock, Crown } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const ProductionPlanning = () => {
  const navigate = useNavigate();
  const [simulations, setSimulations] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ product_id: '', units_planned: '' });
  const [quotaBlocked, setQuotaBlocked] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      const [simsRes, prodsRes, planRes] = await Promise.all([
        planningAPI.getSimulations(user.id || 'demo-user').catch(err => {
          return { data: [] };
        }),
        finishedProductsAPI.getAll(user.id || 'demo-user'),
        fetch(`${API_BASE}/api/user/plan?user_id=${user.id || 'demo-user'}`).then(r => r.json())
      ]);
      
      setSimulations(simsRes.data);
      setProducts(prodsRes.data);
      setQuotaBlocked(planRes.quota_exceeded || false);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await planningAPI.createSimulation({
        product_id: formData.product_id,
        units_planned: parseInt(formData.units_planned)
      }, user.id || 'demo-user');
      
      toast.success('Simulation ajoutée');
      setDialogOpen(false);
      setFormData({ product_id: '', units_planned: '' });
      loadData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette simulation ?')) return;
    try {
      await planningAPI.deleteSimulation(id);
      toast.success('Supprimée');
      loadData();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleGenerateShoppingList = () => {
    if (simulations.length === 0) {
      toast.error('Ajoutez des produits à fabriquer d\'abord');
      return;
    }
    navigate('/production/shopping-list');
  };

  if (loading) return <div className="p-8"><div className="skeleton-shimmer h-64 rounded-lg"></div></div>;

  if (quotaBlocked) {
    return (
      <div className="p-8 max-w-5xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-semibold mb-2">Planification de Production</h1>
          <p className="text-muted-foreground">Prévision des besoins et liste d'approvisionnement</p>
        </div>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-8 text-center">
            <Lock size={40} className="text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-red-800 mb-3">Accès restreint</h2>
            <p className="text-red-700 mb-6 max-w-lg mx-auto">
              Votre inventaire dépasse les limites de la version gratuite. 
              Passez au Premium pour débloquer vos outils de prévision et vos stocks.
            </p>
            <Button className="bg-[#205F79] hover:bg-[#184a5e] gap-2" onClick={() => navigate('/subscription')}>
              <Crown size={18} /> Passer au Premium
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="mb-8">
        <h1 className="text-4xl font-semibold mb-2">Planification de Production</h1>
        <p className="text-muted-foreground">Prévision des besoins et liste d'approvisionnement</p>
      </div>

      <div className="flex gap-4 mb-6">
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="btn-add-simulation">
              <Plus size={18} className="mr-2" /> Ajouter un Produit
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Ajouter un Produit à Fabriquer</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAdd} className="space-y-4 mt-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Produit</label>
                <select 
                  className="flex h-10 w-full rounded-md border border-input bg-white px-3 py-2" 
                  value={formData.product_id} 
                  onChange={(e) => setFormData({...formData, product_id: e.target.value})}
                  required
                >
                  <option value="">-- Sélectionner --</option>
                  {products.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 block">Nombre d'Unités Prévues</label>
                <Input type="number" min="1" value={formData.units_planned} onChange={(e) => setFormData({...formData, units_planned: e.target.value})} required />
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="ghost" onClick={() => setDialogOpen(false)}>Annuler</Button>
                <Button type="submit">Ajouter</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        <Button 
          data-testid="btn-generate-list"
          onClick={handleGenerateShoppingList}
          className="gap-2"
          disabled={simulations.length === 0}
        >
          <ShoppingCart size={18} />
          Générer la Liste d'Approvisionnement
        </Button>

        <Button
          data-testid="btn-export-planning-pdf"
          variant="outline"
          className="gap-2"
          disabled={simulations.length === 0}
          onClick={() => {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            window.open(`${API_BASE}/api/production-planning/pdf?user_id=${user.id || 'demo-user'}`, '_blank');
          }}
        >
          <FileDown size={18} />
          PDF
        </Button>
      </div>

      {simulations.length === 0 ? (
        <Card className="p-12">
          <div className="text-center">
            <AlertTriangle size={48} className="mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground mb-4">Aucune simulation. Ajoutez des produits à fabriquer.</p>
            <Button onClick={() => setDialogOpen(true)}>
              <Plus size={18} className="mr-2" /> Ajouter un produit
            </Button>
          </div>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>Produits à Fabriquer</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {simulations.map(sim => (
                <div key={sim.id} className="flex justify-between items-center p-4 border border-border rounded-md">
                  <div>
                    <p className="font-semibold text-lg">{sim.product_name}</p>
                    <p className="text-sm text-muted-foreground">Unités prévues: {sim.units_planned}</p>
                  </div>
                  <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDelete(sim.id)}>
                    <Trash2 size={16} />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ProductionPlanning;
